import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';

import { preneedPackagesOperations } from '@evdy/web-redux/dist/lib/webPurchase/preneedPackages';
import { purchaseFlowOperations } from '@evdy/web-redux/dist/lib/webPurchase/purchaseFlow';
import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';

import { screenNameRoutes } from './constants';
import { getCookie } from './helperFns';

const { saveLastScreen } = authUserOperations;

// on mount, check if weblead cookie exists and fetch existing weblead
export const useGetWebleadFromCookie = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    const webleadCookie = document?.cookie
      ?.split(';')
      ?.find(cookie => cookie.trim().startsWith('weblead='));
    const webleadId = webleadCookie?.split('=')[1];

    if (webleadId) {
      dispatch(purchaseFlowOperations.fetchWeblead(webleadId));
    }
  }, []);
};

// on mount, if authUser or weblead cookie is not detected navigate back to all packages
export const useDetectWebleadUser = () => {
  const { authUser = {} } = useSelector(({ authUser }) => ({ authUser }));

  useEffect(() => {
    const webleadCookie = getCookie('weblead');

    if (!authUser?.data?._id && !webleadCookie) {
      window.location.href = '/packages';
    }
  }, []);
};

//on mount determine last screen visited by user and redirect
export const useRedirectToLastScreen = ({ dontExecute = false }) => {
  const dispatch = useDispatch();
  const { userData, orderSubmitted } = useSelector(({ authUser }) => ({
    userData: authUser?.data,
    orderSubmitted: authUser?.orderSubmitted
  }));
  const { screen } = userData?.preneed?.lastScreen ?? {};
  const location = global?.window && window?.location;
  const pageTitleParam = location && new URL(location).searchParams.get('pageTitle');
  const paramString = pageTitleParam ? `?=${pageTitleParam}` : '';

  useEffect(() => {
    if (dontExecute) return;
    // if user has completed the purchase flow in a different session, redirect to /your-funeral-package
    if (userData?.preneed?.order && !orderSubmitted) {
      window.location = '/your-funeral-package/';
      return;
    }
    // return if current page is lastScreen
    if (location?.pathname === screenNameRoutes[screen]) return;
    // if not logged in redirect to /get-my-price
    if (!userData && location?.pathname === '/get-my-price/') {
      return;
    } else if (!userData) {
      window.location = '/get-my-price/';
    }
    if (!screen && location) {
      dispatch(saveLastScreen({ screen: 'getEstimates' }));
      window.location = '/get-my-price/';
    } else if (location) {
      // user PUT for preneed.lastScreen.screen handled in useEffect on specific pages
      if (screen === 'getEstimates' || screen === 'viewEstimates') {
        window.location = `${screenNameRoutes[screen]}${paramString}`;
      } else {
        if (!!screenNameRoutes[screen]) {
          browserHistory.push(`${screenNameRoutes[screen]}${paramString}`);
        } else {
          // last screen doesn't exist - update screen and send to `/get-my-price
          dispatch(saveLastScreen({ screen: 'getEstimates' }));
          window.location = '/get-my-price/';
        }
      }
    }
  }, [location, screen]);
};

// hides intercom on mount programatically, can set to hide up until a specific breakpoint
// otherwise hide on all screen sizes
export const useHideIntercom = breakpoint => {
  useEffect(() => {
    if (window?.innerWidth <= (breakpoint || window?.innerWidth)) {
      // eslint-disable-next-line no-undef
      Intercom('update', {
        hide_default_launcher: true
      });
    }
  }, []);
};

//get preneed packages
export const useGetPreneedPackages = () => {
  const dispatch = useDispatch();
  const { preneedPackages = [] } = useSelector(({ webPurchase }) => ({
    preneedPackages: webPurchase.preneedPackages.data?.packages
  }));

  const { getPreneedPackages } = preneedPackagesOperations;

  useEffect(() => {
    if (!preneedPackages?.length) {
      dispatch(getPreneedPackages());
    }
  }, [preneedPackages.length]);
};

// on mount, if authUser is not detected navigate to redirectHref
export const useDetectUser = ({ redirectHref = '/get-my-price', dontExecute = false }) => {
  const { authUser = {} } = useSelector(({ authUser }) => ({ authUser }));

  useEffect(() => {
    if (dontExecute) return;
    if (!authUser?.data?._id) {
      window.location.href = redirectHref;
    }
  }, []);
};
